
import { defineComponent } from 'vue'
import VuePdfEmbed from 'vue-pdf-embed'
import { store } from '@/store'
import type { InstructionWithFile, InstructionConfirmation } from '@/store/instruction'

interface ViewSection {
  sectionId: string
  title: string
  fileName: string
  fileType: 'pdf' | 'docx'
  fileUrl: string | null
}

export default defineComponent({
  name: 'InstructionView',
  components: { VuePdfEmbed },

  data() {
    return {
      loading: true,
      saving: false,
      error: '',
      instruction: null as InstructionWithFile | null,
      checked: [] as boolean[],
      readSections: [] as boolean[],
      activeTab: 0,
      docLoading: true,
    }
  },

  computed: {
    instructionId(): string {
      return this.$route.params.instructionId as string
    },
    signatureName(): string {
      const user = store.getters.getUser
      return user ? `${user.first_name} ${user.last_name}` : ''
    },
    // Sektionen aus der Auflösung; Fallback auf ein Einzeldokument (Legacy/alte Antwort).
    sections(): ViewSection[] {
      const ins = this.instruction
      if (!ins) return []
      const resolved = (ins as any).resolvedSections as any[] | undefined
      if (resolved && resolved.length) {
        return resolved.map((s) => ({
          sectionId: s.sectionId,
          title: s.title,
          fileName: s.fileName,
          fileType: s.fileType,
          fileUrl: s.fileUrl,
        }))
      }
      if (ins.fileKey || ins.fileUrl) {
        return [{
          sectionId: 'legacy',
          title: ins.title,
          fileName: ins.fileName,
          fileType: ins.fileType,
          fileUrl: ins.fileUrl,
        }]
      }
      return []
    },
    activeSection(): ViewSection | null {
      return this.sections[this.activeTab] || null
    },
    confirmationItems(): string[] {
      return this.instruction?.confirmationItems || []
    },
    sectionSummary(): string {
      const n = this.sections.length
      if (n <= 1) return this.sections[0]?.fileName || ''
      return `${n} Dokumente`
    },
    allChecked(): boolean {
      const items = this.confirmationItems
      if (!items.length) return true
      return items.every((_, idx) => this.checked[idx] === true)
    },
    allRead(): boolean {
      return this.sections.every((_, idx) => !this.needsScrollForIdx(idx) || this.readSections[idx] === true)
    },
    myConfirmation(): InstructionConfirmation | undefined {
      return store.getters['instruction/getMyConfirmation'](this.instructionId)
    },
  },

  methods: {
    goBack() {
      this.$router.push({ name: 'lmsCatalog' })
    },

    // Scroll-Pflicht nur für inline angezeigte PDFs.
    needsScrollForIdx(idx: number): boolean {
      const s = this.sections[idx]
      return !!(s && s.fileType === 'pdf' && s.fileUrl)
    },

    setDocRef(el: any, idx: number) {
      if (el) (this as any).docEls[idx] = el as HTMLElement
    },

    onDocScroll(e: Event, idx: number) {
      const el = e.target as HTMLElement
      // 24px Toleranz, damit das Ende zuverlässig erkannt wird
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
        this.markRead(idx)
      }
    },

    onDocRendered(idx: number) {
      this.docLoading = false
      this.$nextTick(() => this.evaluateScroll(idx))
    },

    onDocFailed() {
      this.docLoading = false
    },

    evaluateScroll(idx: number) {
      const el = (this as any).docEls[idx] as HTMLElement | undefined
      // Nur sichtbare Tabs prüfen — versteckte (display:none) haben Höhe 0 und dürfen
      // nicht fälschlich als gelesen gelten.
      if (!el || el.offsetParent === null) return
      // Passt das Dokument ohne Scrollbalken, gilt es als gelesen.
      if (el.scrollHeight <= el.clientHeight + 24) {
        this.markRead(idx)
      }
    },

    markRead(idx: number) {
      if (this.readSections[idx] !== true) this.readSections.splice(idx, 1, true)
    },

    formatDate(ts: number): string {
      if (!ts) return ''
      const d = new Date(ts)
      return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    },

    async confirm() {
      this.error = ''
      if (!this.instruction || !this.allChecked) return
      this.saving = true
      try {
        const checkedItems = this.confirmationItems.map((label, idx) => ({
          label,
          checked: this.checked[idx] === true,
        }))
        await store.dispatch('instruction/CONFIRM', {
          instructionId: this.instructionId,
          payload: {
            checkedItems,
            signatureName: this.signatureName,
            userName: this.signatureName,
          },
        })
      } catch (e: any) {
        this.error = e?.response?.data?.error || 'Die Bestätigung konnte nicht gespeichert werden.'
      } finally {
        this.saving = false
      }
    },

    async downloadCertificate() {
      try {
        const res = await store.dispatch('instruction/GET_CERTIFICATE_URL', {
          instructionId: this.instructionId,
        })
        if (res && res.url) window.open(res.url, '_blank')
      } catch (e) {
        window.alert('Nachweis konnte nicht erstellt werden.')
      }
    },

    async init() {
      try {
        await store.dispatch('instruction/LOAD_MY_CONFIRMATIONS', { force: true })
        this.instruction = await store.dispatch('instruction/SHOW', this.instructionId)
        this.checked = this.confirmationItems.map(() => false)
        this.readSections = this.sections.map(() => false)
        this.docLoading = !!(this.activeSection && this.activeSection.fileType === 'pdf' && this.activeSection.fileUrl)
        // Sicherheitsnetz, falls das @rendered-Event ausbleibt: kurz nach dem Mount prüfen,
        // ob die Dokumente überhaupt scrollbar sind (sonst gelten sie als gelesen).
        this.$nextTick(() => {
          this.sections.forEach((_, idx) => this.evaluateScroll(idx))
          setTimeout(() => this.sections.forEach((_, idx) => this.evaluateScroll(idx)), 1500)
        })
      } catch (e) {
        this.instruction = null
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    // Beim Tab-Wechsel den Scroll zurücksetzen. Die „gelesen"-Prüfung übernimmt das
    // @rendered-Event des neu gemounteten PDFs (sonst würde mit veralteter Höhe geprüft).
    activeTab() {
      const s = this.activeSection;
      // Loader nur zeigen, solange ein PDF gerendert wird; Nicht-PDF (Fallback) sofort anzeigen.
      this.docLoading = !!(s && s.fileType === 'pdf' && s.fileUrl);
      this.$nextTick(() => {
        const el = (this as any).docEls[this.activeTab] as HTMLElement | undefined;
        if (el) el.scrollTop = 0;
      });
    },
  },

  created() {
    // Nicht-reaktiver Halter für die Scroll-Container-Elemente (keine Proxy-Wrapper auf DOM-Knoten).
    (this as any).docEls = {}
  },

  async mounted() {
    await this.init()
  },
})
